import React, { useState, useEffect, useRef } from 'react';
import { Box, Paper, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { wikiTheme } from '../wikiTheme';
import * as wikiService from '../../../services/wikiService';

/**
 * Component for visualizing relationships as a graph
 * Shows the current entry in the center with connected entries around it
 */
const RelationshipGraph = ({ entryId, entryTitle, entryType, relationships = [] }) => {
  const navigate = useNavigate();
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const [relationshipDetails, setRelationshipDetails] = useState([]);
  const [secondLevelRelationships, setSecondLevelRelationships] = useState([]);
  const [hoveredNode, setHoveredNode] = useState(null);
  const [viewOffset, setViewOffset] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [dragDistance, setDragDistance] = useState(0);

  // Limits to prevent too many nodes
  const MAX_FIRST_LEVEL = 12; // Maximum first-level connections to show
  const MAX_SECOND_LEVEL_PER_NODE = 3; // Maximum second-level connections per first-level node
  const MAX_TOTAL_SECOND_LEVEL = 20; // Maximum total second-level nodes

  // Create stable dependency using serialized relationships
  const relationshipsKey = JSON.stringify(relationships.map(r => ({ entryId: r.entryId, type: r.type })));

  useEffect(() => {
    const loadRelationshipDetails = async () => {
      if (relationships.length === 0) {
        setRelationshipDetails([]);
        setSecondLevelRelationships([]);
        return;
      }

      try {
        // Load first level relationships (limit to prevent overcrowding)
        const limitedRelationships = relationships.slice(0, MAX_FIRST_LEVEL);
        
        const details = await Promise.all(
          limitedRelationships.map(async (rel) => {
            try {
              const entry = await wikiService.getWikiEntry(rel.entryId);
              return {
                entryId: rel.entryId,
                relationshipType: rel.type,
                title: entry.title,
                type: entry.type,
                relationships: entry.relationships || []
              };
            } catch (error) {
              console.error(`Error loading relationship entry ${rel.entryId}:`, error);
              return null;
            }
          })
        );
        const firstLevel = details.filter(d => d !== null);
        setRelationshipDetails(firstLevel);

        // Load second level relationships
        const secondLevel = [];
        const seenIds = new Set([entryId, ...firstLevel.map(d => d.entryId)]);
        
        for (const firstLevelEntry of firstLevel) {
          if (secondLevel.length >= MAX_TOTAL_SECOND_LEVEL) break;
          
          // Get relationships for this first-level entry
          const secondLevelRels = firstLevelEntry.relationships
            .filter(rel => !seenIds.has(rel.entryId)) // Don't show already displayed nodes
            .slice(0, MAX_SECOND_LEVEL_PER_NODE);
          
          for (const rel of secondLevelRels) {
            if (secondLevel.length >= MAX_TOTAL_SECOND_LEVEL) break;
            if (seenIds.has(rel.entryId)) continue;
            
            try {
              const entry = await wikiService.getWikiEntry(rel.entryId);
              secondLevel.push({
                entryId: rel.entryId,
                relationshipType: rel.type,
                title: entry.title,
                type: entry.type,
                parentId: firstLevelEntry.entryId // Track which first-level node this connects to
              });
              seenIds.add(rel.entryId);
            } catch (error) {
              console.error(`Error loading second-level entry ${rel.entryId}:`, error);
            }
          }
        }
        
        setSecondLevelRelationships(secondLevel);
      } catch (error) {
        console.error('Error loading relationship details:', error);
      }
    };

    loadRelationshipDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [relationshipsKey]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    const width = canvas.width;
    const height = canvas.height;

    // Clear canvas
    ctx.clearRect(0, 0, width, height);

    // Apply view offset for draggable panning
    ctx.save();
    ctx.translate(viewOffset.x, viewOffset.y);

    // Center node (current entry) - accounting for viewport
    const centerX = width / 2;
    const centerY = height / 2;
    const centerRadius = 40;

    // Colors based on entry type
    const getColor = (type) => {
      switch (type) {
        case 'location':
          return '#42a5f5';
        case 'person':
          return '#66bb6a';
        case 'item':
          return '#ffa726';
        case 'monster':
          return '#e91e63';
        case 'faction':
          return '#ab47bc';
        default:
          return '#d98d2a';
      }
    };

    // Calculate positions for first-level nodes
    const firstLevelAngleStep = (2 * Math.PI) / Math.max(relationshipDetails.length, 1);
    const firstLevelOrbitRadius = 150;
    const firstLevelNodeRadius = 30;
    
    const firstLevelPositions = {};

    // Draw second-level relationships first (so they appear behind first-level)
    const secondLevelOrbitRadius = 270;
    const secondLevelNodeRadius = 20;
    
    secondLevelRelationships.forEach((rel) => {
      // Find the parent node's position
      const parentIndex = relationshipDetails.findIndex(d => d.entryId === rel.parentId);
      if (parentIndex === -1) return;
      
      const parentAngle = firstLevelAngleStep * parentIndex;
      const parentX = centerX + firstLevelOrbitRadius * Math.cos(parentAngle);
      const parentY = centerY + firstLevelOrbitRadius * Math.sin(parentAngle);
      
      // Position second-level nodes around their parent, slightly offset
      const childrenOfParent = secondLevelRelationships.filter(r => r.parentId === rel.parentId);
      const childIndex = childrenOfParent.findIndex(r => r.entryId === rel.entryId);
      const offsetAngle = parentAngle + ((childIndex - (childrenOfParent.length - 1) / 2) * 0.4);
      
      const x = centerX + secondLevelOrbitRadius * Math.cos(offsetAngle);
      const y = centerY + secondLevelOrbitRadius * Math.sin(offsetAngle);
      
      // Draw line from parent to second-level node
      ctx.beginPath();
      ctx.strokeStyle = 'rgba(176, 141, 87, 0.3)';
      ctx.lineWidth = 1;
      ctx.setLineDash([3, 3]);
      ctx.moveTo(parentX, parentY);
      ctx.lineTo(x, y);
      ctx.stroke();
      ctx.setLineDash([]);
      
      // Draw second-level node circle (smaller)
      ctx.beginPath();
      ctx.arc(x, y, secondLevelNodeRadius, 0, 2 * Math.PI);
      ctx.fillStyle = hoveredNode === rel.entryId ? getColor(rel.type) : 'rgba(50, 50, 50, 0.8)';
      ctx.fill();
      ctx.strokeStyle = getColor(rel.type);
      ctx.lineWidth = 2;
      ctx.stroke();
      
      // Draw second-level node text (smaller)
      ctx.fillStyle = wikiTheme.secondary;
      ctx.font = '10px "Crimson Text", serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      
      // Truncate text if too long
      let displayText = rel.title;
      if (displayText.length > 15) {
        displayText = displayText.substring(0, 12) + '...';
      }
      ctx.fillText(displayText, x, y);
    });

    // Draw first-level relationships
    relationshipDetails.forEach((rel, index) => {
      const angle = firstLevelAngleStep * index;
      const x = centerX + firstLevelOrbitRadius * Math.cos(angle);
      const y = centerY + firstLevelOrbitRadius * Math.sin(angle);
      
      // Store position for second-level connections
      firstLevelPositions[rel.entryId] = { x, y };

      // Draw line from center to node
      ctx.beginPath();
      ctx.strokeStyle = wikiTheme.tertiary;
      ctx.lineWidth = 2;
      ctx.setLineDash([5, 5]);
      ctx.moveTo(centerX, centerY);
      ctx.lineTo(x, y);
      ctx.stroke();
      ctx.setLineDash([]);

      // Draw relationship label on line
      const labelX = centerX + (firstLevelOrbitRadius / 2) * Math.cos(angle);
      const labelY = centerY + (firstLevelOrbitRadius / 2) * Math.sin(angle);
      ctx.fillStyle = wikiTheme.primary;
      ctx.font = '10px "Crimson Text", serif';
      ctx.textAlign = 'center';
      ctx.fillText(getRelationshipLabel(rel.relationshipType), labelX, labelY);

      // Draw node circle
      ctx.beginPath();
      ctx.arc(x, y, firstLevelNodeRadius, 0, 2 * Math.PI);
      ctx.fillStyle = hoveredNode === rel.entryId ? getColor(rel.type) : wikiTheme.mediumGray;
      ctx.fill();
      ctx.strokeStyle = getColor(rel.type);
      ctx.lineWidth = 3;
      ctx.stroke();

      // Draw node text
      ctx.fillStyle = wikiTheme.secondary;
      ctx.font = 'bold 12px "Crimson Text", serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      
      // Wrap text if too long
      const maxWidth = firstLevelNodeRadius * 2 - 10;
      const words = rel.title.split(' ');
      let lines = [];
      let currentLine = words[0];

      for (let i = 1; i < words.length; i++) {
        const testLine = currentLine + ' ' + words[i];
        const metrics = ctx.measureText(testLine);
        if (metrics.width > maxWidth) {
          lines.push(currentLine);
          currentLine = words[i];
        } else {
          currentLine = testLine;
        }
      }
      lines.push(currentLine);

      // Draw each line
      const lineHeight = 14;
      const startY = y - ((lines.length - 1) * lineHeight) / 2;
      lines.forEach((line, i) => {
        ctx.fillText(line, x, startY + i * lineHeight);
      });
    });

    // Draw center node (current entry)
    ctx.beginPath();
    ctx.arc(centerX, centerY, centerRadius, 0, 2 * Math.PI);
    ctx.fillStyle = getColor(entryType);
    ctx.fill();
    ctx.strokeStyle = wikiTheme.primary;
    ctx.lineWidth = 4;
    ctx.stroke();

    // Draw center node text
    ctx.fillStyle = wikiTheme.dark;
    ctx.font = 'bold 14px "Crimson Text", serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    
    // Wrap center text
    const maxWidth = centerRadius * 2 - 10;
    const words = entryTitle.split(' ');
    let lines = [];
    let currentLine = words[0];

    for (let i = 1; i < words.length; i++) {
      const testLine = currentLine + ' ' + words[i];
      const metrics = ctx.measureText(testLine);
      if (metrics.width > maxWidth) {
        lines.push(currentLine);
        currentLine = words[i];
      } else {
        currentLine = testLine;
      }
    }
    lines.push(currentLine);

    const lineHeight = 16;
    const startY = centerY - ((lines.length - 1) * lineHeight) / 2;
    lines.forEach((line, i) => {
      ctx.fillText(line, centerX, startY + i * lineHeight);
    });

    ctx.restore();
  }, [relationshipDetails, secondLevelRelationships, entryTitle, entryType, hoveredNode, viewOffset]);

  const getRelationshipLabel = (type) => {
    const typeMap = {
      'related': 'Related',
      'mentioned_in': 'Mentioned',
      'appeared_in': 'Appeared',
      'ally': 'Ally',
      'enemy': 'Enemy',
      'rival': 'Rival',
      'friend': 'Friend',
      'family': 'Family',
      'parent': 'Parent',
      'child': 'Child',
      'sibling': 'Sibling',
      'spouse': 'Spouse',
      'subordinate': 'Subordinate',
      'leader': 'Leader',
      'mentor': 'Mentor',
      'student': 'Student',
      'owns_item': 'Owns',
      'previously_owned': 'Previously Owned',
      'seeks': 'Seeks',
      'protects': 'Protects',
      'hunts': 'Hunts',
      'lives_in': 'Lives In',
      'works_at': 'Works At',
      'member_of': 'Member Of',
      'owned_by': 'Owned By',
      'previously_owned_by': 'Previously Owned By',
      'created_by': 'Created By',
      'discovered_by': 'Discovered By',
      'stolen_by': 'Stolen By',
      'sought_by': 'Sought By',
      'protected_by': 'Protected By',
      'located_at': 'Located At',
      'held_by': 'Held By',
      'hidden_in': 'Hidden In',
      'found_in': 'Found In',
      'part_of': 'Part Of',
      'contains': 'Contains',
      'connected_to': 'Connected',
      'near': 'Near',
      'parent_location': 'Within',
      'child_location': 'Contains',
      'ruled_by': 'Ruled By',
      'threatened_by': 'Threatened By',
      'home_to': 'Home To',
      'workplace_of': 'Workplace',
      'headquarters_of': 'HQ Of',
      'enemy_of': 'Enemy Of',
      'hunted_by': 'Hunted By',
      'serves': 'Serves',
      'commanded_by': 'Commanded By',
      'guards': 'Guards',
      'inhabits': 'Inhabits',
      'summoned_by': 'Summoned By',
      'ally_of': 'Ally Of',
      'defeated_by': 'Defeated By',
      'possesses': 'Possesses',
    };
    return typeMap[type] || type;
  };

  const handleCanvasClick = (event) => {
    // Only navigate if we didn't drag (or dragged very little)
    if (dragDistance > 5) {
      setDragDistance(0);
      return;
    }
    
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    // Don't adjust for viewOffset here - we need screen coordinates
    const mouseX = event.clientX - rect.left;
    const mouseY = event.clientY - rect.top;
    
    // Convert mouse coordinates to canvas coordinates (accounting for transform)
    const x = mouseX - viewOffset.x;
    const y = mouseY - viewOffset.y;

    const centerX = canvas.width / 2;
    const centerY = canvas.height / 2;

    // Check if center node was clicked
    const distToCenter = Math.sqrt((x - centerX) ** 2 + (y - centerY) ** 2);
    if (distToCenter <= 40) {
      return; // Already on this page
    }

    // Check if any first-level relationship node was clicked
    const firstLevelAngleStep = (2 * Math.PI) / Math.max(relationshipDetails.length, 1);
    const firstLevelOrbitRadius = 150;
    const firstLevelNodeRadius = 30;
    
    relationshipDetails.forEach((rel, index) => {
      const angle = firstLevelAngleStep * index;
      const nodeX = centerX + firstLevelOrbitRadius * Math.cos(angle);
      const nodeY = centerY + firstLevelOrbitRadius * Math.sin(angle);
      const dist = Math.sqrt((x - nodeX) ** 2 + (y - nodeY) ** 2);
      
      if (dist <= firstLevelNodeRadius) {
        navigate(`/wiki/${rel.entryId}`);
      }
    });
    
    // Check if any second-level node was clicked
    const secondLevelOrbitRadius = 270;
    const secondLevelNodeRadius = 20;
    
    secondLevelRelationships.forEach((rel) => {
      const parentIndex = relationshipDetails.findIndex(d => d.entryId === rel.parentId);
      if (parentIndex === -1) return;
      
      const parentAngle = firstLevelAngleStep * parentIndex;
      const childrenOfParent = secondLevelRelationships.filter(r => r.parentId === rel.parentId);
      const childIndex = childrenOfParent.findIndex(r => r.entryId === rel.entryId);
      const offsetAngle = parentAngle + ((childIndex - (childrenOfParent.length - 1) / 2) * 0.4);
      
      const nodeX = centerX + secondLevelOrbitRadius * Math.cos(offsetAngle);
      const nodeY = centerY + secondLevelOrbitRadius * Math.sin(offsetAngle);
      const dist = Math.sqrt((x - nodeX) ** 2 + (y - nodeY) ** 2);
      
      if (dist <= secondLevelNodeRadius) {
        navigate(`/wiki/${rel.entryId}`);
      }
    });
  };

  const handleCanvasMouseMove = (event) => {
    if (isDragging) return;
    
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    // Get mouse coordinates and convert to canvas space
    const mouseX = event.clientX - rect.left;
    const mouseY = event.clientY - rect.top;
    const x = mouseX - viewOffset.x;
    const y = mouseY - viewOffset.y;

    const centerX = canvas.width / 2;
    const centerY = canvas.height / 2;
    const firstLevelOrbitRadius = 150;
    const firstLevelNodeRadius = 30;
    const secondLevelOrbitRadius = 270;
    const secondLevelNodeRadius = 20;

    let foundHover = null;

    // Check if hovering over any first-level relationship node
    const firstLevelAngleStep = (2 * Math.PI) / Math.max(relationshipDetails.length, 1);
    relationshipDetails.forEach((rel, index) => {
      const angle = firstLevelAngleStep * index;
      const nodeX = centerX + firstLevelOrbitRadius * Math.cos(angle);
      const nodeY = centerY + firstLevelOrbitRadius * Math.sin(angle);
      const dist = Math.sqrt((x - nodeX) ** 2 + (y - nodeY) ** 2);
      
      if (dist <= firstLevelNodeRadius) {
        foundHover = rel.entryId;
      }
    });
    
    // Check if hovering over any second-level node
    if (!foundHover) {
      secondLevelRelationships.forEach((rel) => {
        const parentIndex = relationshipDetails.findIndex(d => d.entryId === rel.parentId);
        if (parentIndex === -1) return;
        
        const parentAngle = firstLevelAngleStep * parentIndex;
        const childrenOfParent = secondLevelRelationships.filter(r => r.parentId === rel.parentId);
        const childIndex = childrenOfParent.findIndex(r => r.entryId === rel.entryId);
        const offsetAngle = parentAngle + ((childIndex - (childrenOfParent.length - 1) / 2) * 0.4);
        
        const nodeX = centerX + secondLevelOrbitRadius * Math.cos(offsetAngle);
        const nodeY = centerY + secondLevelOrbitRadius * Math.sin(offsetAngle);
        const dist = Math.sqrt((x - nodeX) ** 2 + (y - nodeY) ** 2);
        
        if (dist <= secondLevelNodeRadius) {
          foundHover = rel.entryId;
        }
      });
    }

    if (foundHover !== hoveredNode) {
      setHoveredNode(foundHover);
      canvas.style.cursor = foundHover ? 'pointer' : (isDragging ? 'grabbing' : 'grab');
    }
  };

  const handleMouseDown = (event) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const startX = event.clientX - rect.left;
    const startY = event.clientY - rect.top;
    
    setIsDragging(true);
    setDragDistance(0);
    setDragStart({
      x: startX,
      y: startY
    });
    canvas.style.cursor = 'grabbing';
  };

  const handleMouseUp = () => {
    setIsDragging(false);
    const canvas = canvasRef.current;
    if (canvas) {
      canvas.style.cursor = hoveredNode ? 'pointer' : 'grab';
    }
  };

  const handleMouseDrag = (event) => {
    if (!isDragging) return;
    
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const currentX = event.clientX - rect.left;
    const currentY = event.clientY - rect.top;
    
    const deltaX = currentX - dragStart.x;
    const deltaY = currentY - dragStart.y;
    const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);
    
    setDragDistance(distance);
    setViewOffset(prev => ({
      x: prev.x + deltaX,
      y: prev.y + deltaY
    }));
    
    // Update drag start for next movement
    setDragStart({
      x: currentX,
      y: currentY
    });
  };

  if (relationshipDetails.length === 0) {
    return (
      <Paper sx={{ p: 3, backgroundColor: wikiTheme.dark, textAlign: 'center' }}>
        <Typography sx={{ color: wikiTheme.tertiary, fontFamily: wikiTheme.font, fontStyle: 'italic' }}>
          No relationships to display
        </Typography>
      </Paper>
    );
  }

  return (
    <Paper sx={{ p: 2, backgroundColor: wikiTheme.dark }}>
      <Typography variant="body2" sx={{ mb: 1, color: wikiTheme.tertiary, fontFamily: wikiTheme.font, fontStyle: 'italic' }}>
        Click and drag to pan around the graph. Click nodes to navigate.
      </Typography>
      <Box 
        ref={containerRef}
        sx={{ 
          display: 'flex', 
          justifyContent: 'center',
          overflow: 'hidden',
          borderRadius: '8px'
        }}
      >
        <canvas
          ref={canvasRef}
          width={600}
          height={500}
          onClick={handleCanvasClick}
          onMouseMove={(e) => {
            handleCanvasMouseMove(e);
            handleMouseDrag(e);
          }}
          onMouseDown={handleMouseDown}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
          style={{ 
            border: `2px solid ${wikiTheme.lightGray}`,
            borderRadius: '8px',
            backgroundColor: wikiTheme.mediumGray,
            cursor: 'grab',
            width: '100%',
            maxWidth: '600px',
            height: 'auto'
          }}
        />
      </Box>
    </Paper>
  );
};

export default RelationshipGraph;
